/**
 * Trimmed fork of pmndrs/valtio's vanilla core (`proxy`, `subscribe`,
 * `snapshot`, `ref`) — https://github.com/pmndrs/valtio/blob/main/src/vanilla.ts
 *
 * MIT License, Copyright (c) 2020 Poimandres.
 *
 * What's dropped from upstream and why:
 *  - `proxy-compare` (getUntracked/markToTrack): exists upstream to let
 *    React's `useSnapshot()` know which parts of a snapshot a render
 *    accessed, for fine-grained re-render skipping. We don't ship a React
 *    (or any framework) hook here — the framework-specific bridge lives in
 *    smile-core, built on `@vue/reactivity`'s own track/trigger. Dropping it
 *    removes a transitive dependency that only existed to serve a feature
 *    this package never calls.
 *  - `unstable_enableOp`/the `createOp` on-off toggle: upstream ships op
 *    tracking (the `Op[]` passed to `subscribe()` callbacks) behind an
 *    explicitly "unstable, subject to change" flag. Since this is already a
 *    fork we maintain ourselves, there's no reason to carry that toggle —
 *    op tracking is just always on.
 *  - `unstable_replaceInternalFunction`/`unstable_getInternalStates`: escape
 *    hatches for swapping internals (used upstream for things like devtools
 *    integration and non-default equality checks). Not needed here.
 */

import type { StoreOp } from "@alegradev/smile-core/store"

const isObject = (x: unknown): x is object => typeof x === 'object' && x !== null

type AnyFunction = (...args: unknown[]) => unknown
type Path = (string | symbol)[]

type Listener = (op: StoreOp | undefined, nextVersion: number) => void

type Primitive = string | number | boolean | null | undefined | symbol | bigint

type SnapshotIgnore =
  | Date
  | Map<unknown, unknown>
  | Set<unknown>
  | WeakMap<object, unknown>
  | WeakSet<object>
  | Error
  | RegExp
  | AnyFunction
  | Primitive

export type Snapshot<T> = T extends { $$valtioSnapshot: infer S }
  ? S
  : T extends SnapshotIgnore
    ? T
    : T extends object
      ? { readonly [K in keyof T]: Snapshot<T[K]> }
      : T

type RemoveListener = () => void
type AddListener = (listener: Listener) => RemoveListener

type ProxyState = readonly [
  target: object,
  ensureVersion: (nextCheckVersion?: number) => number,
  addListener: AddListener,
]

const canProxy = (x: unknown): boolean =>
  isObject(x) &&
  !refSet.has(x) &&
  (Array.isArray(x) || !(Symbol.iterator in x)) &&
  !(x instanceof WeakMap) &&
  !(x instanceof WeakSet) &&
  !(x instanceof Error) &&
  !(x instanceof Number) &&
  !(x instanceof Date) &&
  !(x instanceof String) &&
  !(x instanceof RegExp) &&
  !(x instanceof ArrayBuffer) &&
  !(x instanceof Promise)

const createOp = (type: 'set' | 'delete', prop: symbol | string, ...args: unknown[]): StoreOp =>
  [type, [prop], ...args] as StoreOp

const createSnapshot = <T extends object>(target: T, version: number): T => {
  const cache = snapCache.get(target)
  if (cache?.[0] === version) {
    return cache[1] as T
  }
  const snap: object = Array.isArray(target) ? [] : Object.create(Object.getPrototypeOf(target))
  snapCache.set(target, [version, snap])
  Reflect.ownKeys(target).forEach((key) => {
    if (Object.getOwnPropertyDescriptor(snap, key)) {
      // Only the known case is Array.length so far.
      return
    }
    const value = Reflect.get(target, key)
    const { enumerable } = Reflect.getOwnPropertyDescriptor(target, key) as PropertyDescriptor
    const desc: PropertyDescriptor = {
      value,
      enumerable: enumerable as boolean,
      configurable: true,
    }
    if (proxyStateMap.has(value as object)) {
      const [innerTarget, ensureVersion] = proxyStateMap.get(value as object) as ProxyState
      desc.value = createSnapshot(innerTarget, ensureVersion()) as Snapshot<T>
    }
    Object.defineProperty(snap, key, desc)
  })
  return snap as T
}

const createHandler = <T extends object>(
  isInitializing: () => boolean,
  addPropListener: (prop: string | symbol, propValue: unknown) => void,
  removePropListener: (prop: string | symbol) => void,
  notifyUpdate: (op: StoreOp | undefined) => void,
): ProxyHandler<T> => ({
  deleteProperty(target: T, prop: string | symbol): boolean {
    const prevValue = Reflect.get(target, prop)
    removePropListener(prop)
    const deleted = Reflect.deleteProperty(target, prop)
    if (deleted) {
      notifyUpdate(createOp('delete', prop, prevValue))
    }
    return deleted
  },
  set(target: T, prop: string | symbol, value: unknown, receiver: object): boolean {
    const hasPrevValue = !isInitializing() && Reflect.has(target, prop)
    const prevValue = Reflect.get(target, prop, receiver)
    if (
      hasPrevValue &&
      (Object.is(prevValue, value) ||
        (proxyCache.has(value as object) && Object.is(prevValue, proxyCache.get(value as object))))
    ) {
      return true
    }
    removePropListener(prop)
    const nextValue =
      !proxyStateMap.has(value as object) && canProxy(value) ? proxy(value as object) : value
    addPropListener(prop, nextValue)
    Reflect.set(target, prop, nextValue, receiver)
    notifyUpdate(createOp('set', prop, value, prevValue))
    return true
  },
})

const proxyStateMap = new WeakMap<object, ProxyState>()
const refSet = new WeakSet<object>()
const snapCache = new WeakMap<object, [version: number, snap: unknown]>()
const versionHolder = [1] as [number]
const proxyCache = new WeakMap<object, object>()

/** Creates a deeply reactive, mutable proxy object. Nested plain objects/arrays are wrapped lazily on write. */
export function proxy<T extends object>(baseObject: T = {} as T): T {
  if (!isObject(baseObject)) {
    throw new Error('object required')
  }
  const found = proxyCache.get(baseObject) as T | undefined
  if (found) {
    return found
  }
  let version = versionHolder[0]
  const listeners = new Set<Listener>()
  const notifyUpdate = (op: StoreOp | undefined, nextVersion = ++versionHolder[0]): void => {
    if (version !== nextVersion) {
      checkVersion = version = nextVersion
      listeners.forEach((listener) => listener(op, nextVersion))
    }
  }
  let checkVersion = version
  const ensureVersion = (nextCheckVersion = versionHolder[0]): number => {
    if (checkVersion !== nextCheckVersion) {
      checkVersion = nextCheckVersion
      propProxyStates.forEach(([propProxyState]) => {
        const propVersion = propProxyState[1](nextCheckVersion)
        if (propVersion > version) {
          version = propVersion
        }
      })
    }
    return version
  }
  const createPropListener =
    (prop: string | symbol): Listener =>
    (op, nextVersion): void => {
      let newOp: StoreOp | undefined
      if (op) {
        newOp = [...op] as StoreOp
        newOp[1] = [prop, ...(newOp[1] as Path)]
      }
      notifyUpdate(newOp, nextVersion)
    }
  const propProxyStates = new Map<string | symbol, readonly [ProxyState, RemoveListener?]>()
  const addPropListener = (prop: string | symbol, propValue: unknown): void => {
    const propProxyState =
      !refSet.has(propValue as object) && proxyStateMap.get(propValue as object)
    if (propProxyState) {
      if (listeners.size) {
        const remove = propProxyState[2](createPropListener(prop))
        propProxyStates.set(prop, [propProxyState, remove])
      } else {
        propProxyStates.set(prop, [propProxyState])
      }
    }
  }
  const removePropListener = (prop: string | symbol): void => {
    const entry = propProxyStates.get(prop)
    if (entry) {
      propProxyStates.delete(prop)
      entry[1]?.()
    }
  }
  const addListener = (listener: Listener): RemoveListener => {
    listeners.add(listener)
    if (listeners.size === 1) {
      propProxyStates.forEach(([propProxyState, prevRemove], prop) => {
        if (prevRemove) return
        const remove = propProxyState[2](createPropListener(prop))
        propProxyStates.set(prop, [propProxyState, remove])
      })
    }
    const removeListener = (): void => {
      listeners.delete(listener)
      if (listeners.size === 0) {
        propProxyStates.forEach(([propProxyState, remove], prop) => {
          if (remove) {
            remove()
            propProxyStates.set(prop, [propProxyState])
          }
        })
      }
    }
    return removeListener
  }
  let initializing = true
  const handler = createHandler<T>(
    () => initializing,
    addPropListener,
    removePropListener,
    notifyUpdate,
  )
  const proxyObject = new Proxy(baseObject, handler)
  proxyCache.set(baseObject, proxyObject)
  const proxyState: ProxyState = [baseObject, ensureVersion, addListener]
  proxyStateMap.set(proxyObject, proxyState)
  Reflect.ownKeys(baseObject).forEach((key) => {
    const desc = Object.getOwnPropertyDescriptor(baseObject, key) as PropertyDescriptor
    if ('value' in desc && desc.writable) {
      proxyObject[key as keyof T] = baseObject[key as keyof T]
    }
  })
  initializing = false
  return proxyObject
}

/** Current version number of a proxy object — bumps on every mutation reachable from it. */
export function getVersion(proxyObject: unknown): number | undefined {
  const proxyState = proxyStateMap.get(proxyObject as object)
  return proxyState?.[1]()
}

/** Subscribes to changes on a proxy object. The callback receives exactly which paths changed. */
export function subscribe<T extends object>(
  proxyObject: T,
  callback: (ops: StoreOp[]) => void,
  notifyInSync?: boolean,
): () => void {
  const proxyState = proxyStateMap.get(proxyObject as object)
  let promise: Promise<void> | undefined
  const ops: StoreOp[] = []
  const addListener = (proxyState as ProxyState)[2]
  let isListenerActive = false
  const listener: Listener = (op) => {
    if (op) {
      ops.push(op)
    }
    if (notifyInSync) {
      callback(ops.splice(0))
      return
    }
    if (!promise) {
      promise = Promise.resolve().then(() => {
        promise = undefined
        if (isListenerActive) {
          callback(ops.splice(0))
        }
      })
    }
  }
  const removeListener = addListener(listener)
  isListenerActive = true
  return () => {
    isListenerActive = false
    removeListener()
  }
}

/** A deeply readonly, frozen snapshot of the current state — cached per version, cheap to call repeatedly. */
export function snapshot<T extends object>(proxyObject: T): Snapshot<T> {
  const proxyState = proxyStateMap.get(proxyObject as object)
  const [target, ensureVersion] = proxyState as ProxyState
  return createSnapshot(target, ensureVersion()) as Snapshot<T>
}

/** Marks an object to be excluded from proxying — kept as a raw reference instead of being deeply wrapped. */
export function ref<T extends object>(obj: T): T & { $$valtioSnapshot: T } {
  refSet.add(obj)
  return obj as T & { $$valtioSnapshot: T }
}

const readonlyCache = new WeakMap<object, object>()

/**
 * A live, deeply readonly VIEW of a proxy object. Unlike `snapshot()` (a
 * frozen point-in-time copy), this stays connected to the underlying
 * proxy — reads always reflect the current value, `subscribe()` still
 * fires for changes made through the writable proxy elsewhere — it just
 * can't be written through *this* reference. That's the intended shape
 * for exposing an owner-mutable store to consumers who shouldn't mutate
 * it directly (e.g. `SessionStore` keeps the writable proxy private and
 * exposes `readonly(state)` via its public view).
 */
export function readonly<T extends object>(proxyObject: T): T {
  const cached = readonlyCache.get(proxyObject)
  if (cached) return cached as T

  const view = new Proxy(proxyObject as object, {
    get(target, prop, receiver): unknown {
      const value = Reflect.get(target, prop, receiver)
      return isObject(value) ? readonly(value) : value
    },
    set(): boolean {
      throw new Error('[reactivity] this store is readonly')
    },
    deleteProperty(): boolean {
      throw new Error('[reactivity] this store is readonly')
    },
  })
  readonlyCache.set(proxyObject, view)
  return view as T
}
